import { Link } from 'react-router-dom';
import SystemCard from './SystemCard';
import type { SystemInfo } from '../types';

const systems: SystemInfo[] = [
  {
    id: 'factsagent',
    name: 'FactsAgent',
    description: '9-step transcript processing pipeline that extracts, verifies, and formats facts from interviews',
    icon: '📝',
    color: 'blue',
    enabled: true,
  },
  {
    id: 'cppages',
    name: 'CP Pages Generator',
    description: 'Treatment page content generator with 7 specialized types for rehabilitation facilities',
    icon: '🏥',
    color: 'purple',
    enabled: true,
  },
  {
    id: 'images',
    name: 'Content Image Generator',
    description: 'Marketing image generator with customizable templates and backgrounds',
    icon: '🖼️',
    color: 'pink',
    enabled: true,
  },
  {
    id: 'content',
    name: 'Content Generation System',
    description: 'SEO article generator with 11 article types and multi-threaded processing',
    icon: '📄',
    color: 'yellow',
    enabled: true,
  },
  {
    id: 'topicalmap',
    name: 'Topical Map Generator',
    description: 'SEO site architecture generator with 6 specialized generators for comprehensive topic coverage',
    icon: '🗺️',
    color: 'green',
    enabled: true,
  },
  {
    id: 'serviceareapages',
    name: 'Service Area Pages',
    description: 'Generate localized addiction treatment resource pages for different geographic locations',
    icon: '📍',
    color: 'teal',
    enabled: true,
  },
];

export default function Dashboard() {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">BrandNorth</h1>
              <p className="mt-1 text-sm text-gray-500">
                AI-Powered Content Generation Platform
              </p>
            </div>
            <div className="flex items-center space-x-4">
              <div className="text-sm text-gray-500">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  API Connected
                </span>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Section */}
        <div className="mb-8">
          <h2 className="text-2xl font-semibold text-gray-900 mb-2">
            Welcome to BrandNorth
          </h2>
          <p className="text-gray-600">
            Select a system below to start generating content. Each system is designed for specific content generation tasks.
          </p>
        </div>

        {/* Systems Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {systems.map((system) => (
            <SystemCard key={system.id} system={system} />
          ))}
        </div>

        {/* Stats Section */}
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="card">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
                  <span className="text-2xl">🚀</span>
                </div>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Total Jobs</p>
                <p className="text-2xl font-semibold text-gray-900">0</p>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                  <span className="text-2xl">✅</span>
                </div>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Completed</p>
                <p className="text-2xl font-semibold text-gray-900">0</p>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
                  <span className="text-2xl">⚡</span>
                </div>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Active Jobs</p>
                <p className="text-2xl font-semibold text-gray-900">0</p>
              </div>
            </div>
          </div>
        </div>

        {/* Getting Started */}
        <div className="mt-12 card bg-primary-50 border-primary-200">
          <h3 className="text-lg font-semibold text-primary-900 mb-2">
            Getting Started
          </h3>
          <p className="text-primary-700 mb-4">
            Currently, FactsAgent is available for processing interview transcripts.
            More systems will be available soon.
          </p>
          <Link
            to="/factsagent"
            className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            Start with FactsAgent →
          </Link>
        </div>
      </main>
    </div>
  );
}
